import { dictionaryApi } from './dictionaryClient';
import {
  normalizeAssociatedBarna,
  normalizePratyahar,
  type AssociatedBarna,
  type Pratyahar,
} from './pratyaharApi';

export type { AssociatedBarna };

export type BartikPadachhedPratyaharLink = {
  id: number;
  ashtadhyayiBartikPadachhedId: number;
  pratyaharId: number;
  selectedBarnas: AssociatedBarna[];
  pratyahar: Pratyahar | null;
};

export type CreateBartikPadachhedPratyaharLinkBody = {
  ashtadhyayiBartikPadachhedId: number;
  pratyaharId: number;
  selectedBarnas: AssociatedBarna[];
};

export type UpdateBartikPadachhedPratyaharLinkBody = {
  selectedBarnas?: AssociatedBarna[];
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizeBartikPadachhedPratyaharLink(raw: unknown): BartikPadachhedPratyaharLink | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiBartikPadachhedId =
    num(raw, 'ashtadhyayiBartikPadachhedId') ?? num(raw, 'ashtadhyayi_bartik_padachhed_id');
  const pratyaharId = num(raw, 'pratyaharId') ?? num(raw, 'pratyahar_id');
  if (id == null || ashtadhyayiBartikPadachhedId == null || pratyaharId == null) return null;

  const selectedRaw = raw.selectedBarnas ?? raw.selected_barnas;
  const selectedBarnas = Array.isArray(selectedRaw)
    ? selectedRaw.map(normalizeAssociatedBarna).filter((x): x is AssociatedBarna => x != null)
    : [];

  const pratyaharRaw = raw.pratyahar;
  const pratyahar = pratyaharRaw ? normalizePratyahar(pratyaharRaw) : null;

  return {
    id,
    ashtadhyayiBartikPadachhedId,
    pratyaharId,
    selectedBarnas,
    pratyahar,
  };
}

function normalizeList(data: unknown): BartikPadachhedPratyaharLink[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizeBartikPadachhedPratyaharLink)
    .filter((x): x is BartikPadachhedPratyaharLink => x != null);
}

export async function listBartikPadachhedPratyaharLinks(filters?: {
  ashtadhyayiBartikPadachhedId?: number;
  pratyaharId?: number;
}): Promise<BartikPadachhedPratyaharLink[]> {
  const params: Record<string, number> = {};
  if (filters?.ashtadhyayiBartikPadachhedId != null) {
    params.ashtadhyayiBartikPadachhedId = filters.ashtadhyayiBartikPadachhedId;
  }
  if (filters?.pratyaharId != null) {
    params.pratyaharId = filters.pratyaharId;
  }
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-bartik-padachhed-pratyahar', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return normalizeList(res.data);
}

export async function getBartikPadachhedPratyaharLink(id: number): Promise<BartikPadachhedPratyaharLink> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-bartik-padachhed-pratyahar/${id}`);
  const row = normalizeBartikPadachhedPratyaharLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-pratyahar link response');
  return row;
}

export async function createBartikPadachhedPratyaharLink(
  body: CreateBartikPadachhedPratyaharLinkBody
): Promise<BartikPadachhedPratyaharLink> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-bartik-padachhed-pratyahar', body);
  const row = normalizeBartikPadachhedPratyaharLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-pratyahar link response');
  return row;
}

export async function updateBartikPadachhedPratyaharLink(
  id: number,
  body: UpdateBartikPadachhedPratyaharLinkBody
): Promise<BartikPadachhedPratyaharLink> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-bartik-padachhed-pratyahar/${id}`, body);
  const row = normalizeBartikPadachhedPratyaharLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-pratyahar link response');
  return row;
}

export async function deleteBartikPadachhedPratyaharLink(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-bartik-padachhed-pratyahar/${id}`);
}
